/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import SEO from "@/components/site/SEO";

const SECTIONS = [
  {
    h: "What are cookies?",
    p: "Cookies are small text files stored on your device when you visit a website. They help us remember you, keep you signed in, and understand how our site is used so we can make it better.",
  },
  {
    h: "How we use cookies",
    p: "OneDial Africa (Scitygate Telecom UK Ltd) uses cookies in four categories. Strictly necessary cookies are always on — without them the site cannot function. The others run only if you agree.",
  },
];

const CATEGORIES = [
  {
    name: "Strictly necessary",
    purpose: "Keep you signed in, remember your session, secure form submissions.",
    examples: "access_token, cust_access_token, csrf_token",
    optional: false,
  },
  {
    name: "Preferences",
    purpose: "Remember UI choices like preferred currency, country, and cookie-consent state.",
    examples: "onedial_cookie_consent, onedial_locale",
    optional: true,
  },
  {
    name: "Analytics",
    purpose: "Aggregated usage statistics so we can improve the product. We use Google Analytics 4 only when you opt in.",
    examples: "_ga, _ga_*",
    optional: true,
  },
  {
    name: "Marketing",
    purpose: "Measure the effectiveness of our advertising so we don't waste your time with irrelevant ads.",
    examples: "_fbp, fr (Meta), _gcl_au (Google Ads)",
    optional: true,
  },
];

export default function CookiePolicy() {
  return (
    <div className="bg-brand-bgSoft text-brand-ink" data-testid="cookie-policy-page">
      <SEO title="Cookie Policy" path="/cookie-policy"
        description="How OneDial Africa uses cookies, what categories we set, and how to control your preferences." />

      <section className="bg-brand-ink text-white">
        <div className="container-page py-16 lg:py-20">
          <p className="label-eyebrow text-brand-green">Legal</p>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl">Cookie Policy</h1>
          <p className="mt-3 text-white/70 text-sm">Last updated: 1 June 2026</p>
        </div>
      </section>

      <section className="container-page py-12 max-w-3xl space-y-8 text-sm leading-relaxed">
        {SECTIONS.map((s) => (
          <div key={s.h}>
            <h2 className="font-display font-extrabold text-xl">{s.h}</h2>
            <p className="mt-2 text-brand-ink/85">{s.p}</p>
          </div>
        ))}

        <div>
          <h2 className="font-display font-extrabold text-xl">Cookie categories we use</h2>
          <div className="mt-4 rounded-2xl border border-brand-line bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-brand-bgSoft text-left text-xs uppercase tracking-wider text-brand-muted">
                <tr><th className="py-3 px-4">Category</th><th className="py-3 px-4">Purpose</th><th className="py-3 px-4">Examples</th><th className="py-3 px-4">Optional</th></tr>
              </thead>
              <tbody className="divide-y divide-brand-line">
                {CATEGORIES.map((c) => (
                  <tr key={c.name}>
                    <td className="py-3 px-4 font-semibold">{c.name}</td>
                    <td className="py-3 px-4 text-brand-ink/80">{c.purpose}</td>
                    <td className="py-3 px-4 font-mono text-[11px] text-brand-muted">{c.examples}</td>
                    <td className="py-3 px-4">{c.optional ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="font-display font-extrabold text-xl">Managing your preferences</h2>
          <p className="mt-2 text-brand-ink/85">
            You can change your cookie preferences any time by clicking <button type="button" onClick={() => {
              try { window.localStorage.removeItem("onedial_cookie_consent"); window.location.reload(); }
              catch (err) {
                // Safari private mode may throw — fallback to a plain reload.
                if (process.env.NODE_ENV !== "production") console.warn("cookie reset", err);
                window.location.reload();
              }
            }} className="underline text-brand-greenDark" data-testid="cookie-reset-btn">"Reset cookie preferences"</button>.
            Most browsers also let you block cookies entirely — see the help section of your browser for details. Blocking strictly-necessary cookies may break parts of the site.
          </p>
        </div>

        <div>
          <h2 className="font-display font-extrabold text-xl">Contact</h2>
          <p className="mt-2 text-brand-ink/85">
            If you have questions about this policy, email us at <a className="text-brand-greenDark underline" href="mailto:privacy@onedialafrica.com">privacy@onedialafrica.com</a> or read our <Link to="/privacy-policy" className="text-brand-greenDark underline">Privacy Policy</Link>.
          </p>
          <p className="mt-3 text-xs text-brand-muted">
            OneDial Africa is a trading name of Scitygate Telecom UK Ltd, registered in England &amp; Wales under Company No. <span data-testid="cookie-company-number">16468736</span>. Registered office: 2nd Floor, Halifax House, 93–101 Bridge Street, Manchester M3 2GX.
          </p>
        </div>
      </section>
    </div>
  );
}
